import { ResponsiveImage } from '@/components';
import { useEffect, useMemo, useRef } from 'react';
import { MediaGalleryImageModel } from './MediaGalleryImagesList';
import classNames from 'classnames';
import CheckboxBlankCircle from '@/assets/checkboxBlankCircle.svg';
import CheckboxCircle from '@/assets/checkboxCircle.svg';

interface MediaGalleryLeftSideModel {
  imageList: MediaGalleryImageModel[];
  activeImageIndex?: number | null;
  selectedImages: MediaGalleryImageModel[];
  setActiveImage: (image: MediaGalleryImageModel) => void;
  selectImage: (image: MediaGalleryImageModel) => void;
  showSaveToScrapbook?: boolean;
  showDownloadBtn?: boolean;
}

const MediaGalleryLeftSide = ({
  imageList,
  activeImageIndex,
  selectedImages,
  setActiveImage,
  selectImage,
  showSaveToScrapbook = false,
  showDownloadBtn = false,
}: MediaGalleryLeftSideModel) => {
  const leftSide = useRef<HTMLDivElement>(null);
  const groupedImagesList = useMemo(() => {
    const result: [MediaGalleryImageModel[], MediaGalleryImageModel[]] = [[], []];
    imageList.forEach((item, index) => {
      result[index % 2].push(item);
    });
    return result;
  }, [imageList]);

  useEffect(() => {
    if (activeImageIndex != null) {
      scrollToImage(activeImageIndex);
    }
  }, [activeImageIndex]);

  const smallImageLoaded = (image: MediaGalleryImageModel) => {
    if (activeImageIndex != null && activeImageIndex === image.index) {
      scrollToImage(activeImageIndex);
    }
  };

  // Scroll the small image into view
  const scrollToImage = (index: number) => {
    if (!leftSide.current) return;
    const smallImageElement = leftSide.current.querySelector(`[data-image-index="${index}"]`);
    if (smallImageElement) {
      smallImageElement.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
      });
    }
  };

  const selectedIds = useMemo(() => new Set(selectedImages.map((i) => i.mediaId)), [selectedImages]);

  return (
    <div ref={leftSide} className="flex h-full py-2">
      {groupedImagesList.map((imageGroup, imageGroupIndex) => (
        <div key={imageGroupIndex} className="flex w-1/2 flex-col">
          {imageGroup.map((image) => {
            const isSelected = selectedIds.has(image.mediaId);
            return (
              <div
                key={image.mediaId}
                className="group relative p-2"
                data-image-index={image.index}
                onClick={() => setActiveImage(image)}
              >
                <ResponsiveImage
                  image={image}
                  widths={{
                    xs: 200,
                    sm: 200,
                    md: 400,
                    lg: 200,
                    xl: 250,
                  }}
                  imgProps={{
                    className: classNames(
                      'cursor-pointer border-2 border-solid p-px transition-colors',
                      image.index === activeImageIndex ? 'border-gray-500' : 'border-transparent',
                    ),
                    onLoad: () => smallImageLoaded(image),
                  }}
                />
                {(showSaveToScrapbook || showDownloadBtn) && (
                  <button
                    className={classNames(
                      'absolute top-3 left-3 cursor-pointer transition-opacity duration-200 group-hover:opacity-100 pointer-fine:opacity-0',
                      isSelected && 'opacity-100 pointer-fine:opacity-100',
                    )}
                    onClick={(e) => {
                      e.stopPropagation();
                      selectImage(image);
                    }}
                  >
                    {isSelected ? (
                      <CheckboxCircle className="h-6 w-6" />
                    ) : (
                      <CheckboxBlankCircle className="h-6 w-6 fill-white stroke-black" />
                    )}
                  </button>
                )}
              </div>
            );
          })}
        </div>
      ))}
    </div>
  );
};

export default MediaGalleryLeftSide;
